import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ConfigInterface, ConfigParticipantCopySupportByNotificationType } from '../../interfaces/config.interface';
import { OdataConfigInstanceToken, OdataConfigInterface } from '../../interfaces/odata-config.interface';
import { ConfigModel } from '../../models/low-level/config.model';
import { EventService } from '../event/event.service';
import emojiRegex from 'emoji-regex';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';

@Injectable({
    providedIn: 'root',
})
export class ConfigService implements ConfigInterface {
    private models: ConfigModel[] = [];
    public isInitialized = false;
    private emojisRegexString = ConfigConstants.defaultEmojiRegex;
    public defaultAllowedMimeTypes = ['image/tiff', 'image/jpg', 'image/jpeg', 'application/pdf'];

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(OdataConfigInstanceToken) private readonly odataConfigService: OdataConfigInterface,
        private readonly eventService: EventService,
        private readonly localStorage: LocalStorageCommonService,
        private readonly paceInsights: PaceInsightsService,
    ) {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    /**
     * Initialize the config service on app-start.ts
     *
     * @returns an observable of the config models
     * @throws {Error} if service is not initialized
     */
    public init(): Observable<ConfigModel[]> {
        return this.odataConfigService.getConfig().pipe(
            tap((models) => {
                this.paceInsights.trackEvent('loaded_config', Object.fromEntries(models.map((m) => [m.key, m.value])));
                this.models = models;
                this.isInitialized = true;

                // initialize emoji regex with failsafe
                const emojisRegexString =
                    this.getConfigModelByKey(ConfigConstants.configKeyEmojisRegex)?.value ?? this.emojisRegexString;
                try {
                    // Suppressing warning about unused variable since we do not actually need the regex here, we just
                    // need to ensure that the value can be parsed into a regex

                    // Matches UTF-16 High and Low surrogate pairs to detect emojis (characters above U+FFFF)
                    const surrogateEmojiRegex = `[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]`;
                    const emojiRegexFromLibrary = emojiRegex().source;
                    this.emojisRegexString = `${surrogateEmojiRegex}|${emojisRegexString}|${emojiRegexFromLibrary}`;
                } catch {
                    this.log.error(`ConfigService: init: Error parsing emoji regex: '${emojisRegexString}'`);
                }

                // Store the backend in local storage for easier debugging
                this.localStorage.preInitializationStorageSet(
                    ConfigConstants.configKeyBackendName,
                    this.getBackendName(),
                );
            }),
        );
    }

    /**
     * Common method to get the ConfigModel value using Key
     *
     * @param key The key of the ConfigModel
     * @returns The ConfigModel or undefined if not found
     * @throws {Error} if service is not initialized
     */
    private getConfigModelByKey(key: string): ConfigModel | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getConfigModelByKey: Trying to call ConfigService before initialization for key '${key}'!`,
            );
        }

        return this.models.find((model) => model.key === key);
    }

    public getMaterialErrorCodeCountMax(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialErrorCodeCountMax: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialErrorCodeCountMax);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    public getCseEmailFax(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCseEmailFax: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCseEmailFax);
        return model ? model.value : '';
    }

    /**
     * get the flag to show refresh XML button for forms
     *
     * @returns true if the refresh XML button should be shown
     * @throws {Error} if service is not initialized
     */
    public getShowRefreshXMLButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowRefreshXMLButton: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowRefreshXMLButton);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayCustomerContact(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCustomerContact: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayCustomerContact);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowLanguageSelectionDropdown(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowLanguageSelectionDropdown: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowLanguageSelectionDropdown);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEscalationEmail(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEscalationEmail: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEscalationEmail);
        return model ? model.value : '';
    }

    public getInstructionsUrl(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getInstructionsService: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyInstructionsUrl);
        return model ? model.value : '';
    }

    /**
     * get the flag to show instructions documentation link for forms
     *
     * @returns true if the instructions documentation link should be shown
     * @throws {Error} if service is not initialized
     */
    public getShowDocumentationLink(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowDocumentationLink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowDocumentationLink);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getUserFrontId(): string {
        const model = this.getConfigModelByKey(ConfigConstants.configKeyUserFrontId);
        return model ? model.value : '';
    }

    public getMaxInterimsCount(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getMaxInterimsCount: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaxInterimsCount);
        return model ? parseInt(model.value, 10) : 0;
    }

    public getComplaintNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getComplaintNotificationType: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyComplaintNotifTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    /**
     * Get the CSE Currency string
     *
     * @returns the CSE Currency string
     * @throws {Error} if service is not initialized
     */
    public getExpenseCurrency(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getExpenseCurrency: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyExpenseCurrency);
        return model ? model.value : '';
    }

    /**
     * Gets the CSE Country
     *
     * @returns the CSE Country
     * @throws {Error} if service is not initialized
     */
    public getCseCountry(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCseCountry: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCseCountry);
        return model ? model.value : '';
    }

    public getShowInfoToCCC(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getShowInfoToCCC: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowInfoToCCC);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Gets the LegalCheckIEC62353Text to display for equipment checkbox
     *
     * @returns the LegalCheckIEC62353Text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckIEC62353Text(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckIEC62353Text: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalIEC62353Text);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckIEC62353Code to compare with Equipment.Division
     *
     * @returns the LegalCheckIEC62353Code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckIEC62353Code(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckIEC62353Code: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalIEC62353Code);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckPCResistanceText to display additional control label for LegalCheckIEC62353Text
     *
     * @returns the LegalCheckPCResistanceText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPCResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPCResistanceText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalPCResistanceText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckPCResistanceCode to compare with Equipment.Division
     *
     * @returns the LegalCheckPCResistanceCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPCResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPCResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalPCResistanceCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckDeviceLeakageText to display additional control label for LegalCheckIEC62353Text
     *
     * @returns the LegalCheckDeviceLeakageText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDeviceLeakageText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDeviceLeakageText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalDeviceLeakageText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckDeviceLeakageCode to compare with Equipment.Division
     *
     * @returns the LegalCheckDeviceLeakageCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDeviceLeakageCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDeviceLeakageCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalDeviceLeakageCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckXRayText to display for equipment checkbox only for Country-Germany
     *
     * @returns the LegalCheckXRayText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckXRayText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckXRayText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalXRayText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckXRayCode to compare with Equipment.Division only for Country-Germany
     *
     * @returns the LegalCheckXRayCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckXRayCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckXRayCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalXRayCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckRadioProtectionText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckRadioProtectionText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckRadioProtectionText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckRadioProtectionText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalRadioProtectionText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckRadioProtectionCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckRadioProtectionCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckRadioProtectionCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckRadioProtectionCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalRadioProtectionCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckInstallationCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckInstallationCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInstallationCheckText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInstallationCheckText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalInstallationCheckText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckInstallationCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckInstallationCheckCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInstallationCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInstallationCheckCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalInstallationCheckCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckStatusCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckStatusCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckStatusCheckText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckStatusCheckText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalStatusCheckText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckStatusCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckStatusCheckCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckStatusCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckStatusCheckCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalStatusCheckCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckConsistencyCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckConsistencyCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyCheckText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckConsistencyCheckText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalConsistencyCheckText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckConsistencyCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the legal check consistency check code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckConsistencyCheckCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalConsistencyCheckCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckPartiallyCheckedText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check partially checked text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartiallyCheckedText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPartiallyCheckedText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalPartiallyCheckedText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckPartiallyCheckedCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the legal check partially checked code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartiallyCheckedCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPartiallyCheckedCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalPartiallyCheckedCode);
        return model ? model.value : '';
    }

    /**
     * Gets the getLegalCheckAcceptanceTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check acceptance test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckAcceptanceTestCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckAcceptanceTestCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalAcceptanceTestCode);
        return model ? model.value : '';
    }

    /**
     * Gets the getLegalCheckPartialAcceptanceTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check partial acceptance test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartialAcceptanceTestCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPartialAcceptanceTestCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalPartialAcceptanceTestCode);
        return model ? model.value : '';
    }

    /**
     * Gets the getLegalCheckHealthCheckCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check health check code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckHealthCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckHealthCheckCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalHealthCheckCode);
        return model ? model.value : '';
    }

    /**
     * Gets the getLegalCheckConsistencyTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check consistency test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyTestCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckConsistencyTestCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalConsistencyTestCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckNEN1010Text to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check NEN1010 text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckNEN1010Text(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckNEN1010Text: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalNEN1010Text);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckNEN1010Code to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check NEN1010 code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckNEN1010Code(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckNEN1010Code: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalNEN1010Code);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckK3RoomText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check K3 room text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckK3RoomText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckK3RoomText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalK3RoomText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckK3RoomCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check K3 room code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckK3RoomCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckK3RoomCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalK3RoomCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckMeasurementPerformedText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check measurement performed text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementPerformedText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementPerformedText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalMeasurementPerformedText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckMeasurementPerformedCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check measurement performed code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementPerformedCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementPerformedCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalMeasurementPerformedCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckInsulationResistanceText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check insulation resistance text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInsulationResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInsulationResistanceText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalInsulationResistanceText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckInsulationResistanceCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check insulation resistance code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInsulationResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInsulationResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalInsulationResistanceCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckMeasurementNotNeededText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check measurement not needed text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementNotNeededText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementNotNeededText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalMeasurementNotNeededText);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckMeasurementNotNeededCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check measurement not needed code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementNotNeededCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementNotNeededCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalMeasurementNotNeededCode);
        return model ? model.value : '';
    }

    /**
     * Break time between end-of-work and start-of-work.
     *
     * @returns the break time in hours
     * @throws {Error} if service is not initialized
     */
    public getEowSowBreakHours(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEowSowBreakHours: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEowSowBreakHours);
        return model ? parseInt(model.value, 10) : 0;
    }

    /**
     * Time in hours that end-of-work can be moved into the future
     *
     * @returns the flexible hours for end-of-work
     * @throws {Error} if service is not initialized
     */
    public getEowFlexibleHours(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEowFlexibleHours: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEowFlexibleHours);
        return model ? parseInt(model.value, 10) : ConfigConstants.eowFlexibleHours;
    }

    /**
     * Determines the display of equipment based on the notification's taskGroup and the values from configSet
     * For eg. taskGroup = 'APPS' and configSet key = 'ShowEquipment' has value 'CSE,APPS' , then return true
     *
     * @param taskGroup the task group of the notification
     * @returns true if the equipment should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showEquipment(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showEquipment: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowEquipment);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : true;
    }

    /**
     * Determines the display of material based on the notification's taskGroup and the values from configSet
     * For eg. taskGroup = 'APPS' and configSet key = 'ShowMaterialConsumption' has value 'CSE' , then return false
     *
     * @param taskGroup the task group of the notification
     * @returns true if the material should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMaterialConsumption(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: showMaterialConsumption: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowMaterialConsumption);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : true;
    }

    /**
     * Determines the display of MeasurementTools based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the measurement tools should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMeasurementTools(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showMeasurementTools: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowMeasurementTools);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : true;
    }

    /**
     * Determines the display of ParticipantList based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the participant list should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showParticipantList(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showParticipantList: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowParticipantList);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : false;
    }

    /**
     * Determines the display of Follow-Up based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the follow-up should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showFollowup(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showFollowup: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowFollowup);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : false;
    }

    public showFollowupLongText(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showFollowupLongText: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowFollowupLongText);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public showFollowupAdditionalInfo(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: showFollowupAdditionalInfo: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowFollowupAdditionalInfo);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Determines the display of Measuring Points based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the measuring points should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMeasuringPoints(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showMeasuringPoints: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowMeasuringPoints);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : false;
    }

    public getFollowupDefaultAdditionalTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowupDefaultAdditionalTime: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFollowupDefaultAdditionalTime);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    public getFollowupMinAdditionalTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowupMinAdditionalTime: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFollowupMinAdditionalTime);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    public getFollowupAdditionalTimeStep(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowupAdditionalTimeStep: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFollowupAdditionalTimeStep);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    public getAppsTaskCode(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppsTaskCode: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppsTaskCode);
        return model ? model.value : '';
    }

    public getStatusAfterFixValueForQCFlag(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getStatusAfterFixValueForQCFlag: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyStatusAfterFixValueForQCFlag);
        return model ? model.value : '';
    }

    /**
     * get the ETA limit from config service
     *
     * @returns the ETA limit in hours
     * @throws {Error} if service is not initialized
     */
    public getEtaLimitMinutes(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEtaLimitMinutes: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEtaLimitMinutes);
        return model ? parseInt(model.value, 10) : ConfigConstants.defaultEtaLimitMinutes;
    }

    public getMaterialHistoryLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialHistoryLimit: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialHistoryLimit);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    /**
     * Configuration to determine how many days back in time
     * is the limit to retrieve notifications
     *
     * @example 30 retrieve notifications for the last 30 days
     * Used for DocBox side menu to display notifications history list
     *
     * @returns number representing the limit of days i.e.: 30, 60, 90
     * @throws {Error} if service is not initialized
     */
    public getNotificationsHistoryDaysLimit(): number {
        const maxNotificationHistoryDays = 30;
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getNotificationsHistoryDaysLimit: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyNotificationHistoryDays);
        return model ? parseInt(model.value, 10) : maxNotificationHistoryDays;
    }

    /**
     * get the headquarter detail line 1 from config service
     *
     * @returns the headquarter detail line 1
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ1(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ1: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQ1);
        return model ? model.value : '';
    }

    /**
     * get the headquarter detail line 2 from config service
     *
     * @returns the headquarter detail line 2
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ2(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ2: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQ2);
        return model ? model.value : '';
    }

    /**
     * get the headquarter detail line 3 from config service
     *
     * @returns the headquarter detail line 3
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ3(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ3: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQ3);
        return model ? model.value : '';
    }

    /**
     * get the headquarter detail line 4 from config service
     *
     * @returns the headquarter detail line 4
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ4(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ4: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQ4);
        return model ? model.value : '';
    }

    /**
     * get the headquarter detail link label from config service
     *
     * @returns the headquarter detail link label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ5(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ5: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQ5);
        return model ? model.value : '';
    }

    /**
     * get the headquarter detail link from config service
     *
     * @returns the headquarter detail link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ5Link(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ5Link: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQ5Link);
        return model ? model.value : '';
    }

    /**
     * get the headquarter detail telephone number details from config service
     *
     * @returns the headquarter detail telephone number details
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQTel(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQTel: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoHQTel);
        return model ? model.value : '';
    }

    /**
     * get the Support hours for corporate Information for NGMAA
     *
     * @returns the Support hours for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportHours(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportHours: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoSupportHours);
        return model ? model.value : '';
    }

    /**
     * get the Support Email for corporate Information for NGMAA
     *
     * @returns the Support Email for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportEmail(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportEmail: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoSupportEmail);
        return model ? model.value : '';
    }

    /**
     * get the Support link for corporate Information for NGMAA
     *
     * @returns the Support link for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportLink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoSupportLink);
        return model ? model.value : '';
    }

    /**
     * get the Support create ticket link for corporate Information for NGMAA
     *
     * @returns the Support create ticket link for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportCreateTicketLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportCreateTicketLink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoSupportCreateTicketLink);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 1 from config service
     *
     * @returns the legal info line 1
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo1(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo1: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo1);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 2 from config service
     *
     * @returns the legal info line 2
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo2(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo2: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo2);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 3 from config service
     *
     * @returns the legal info line 3
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo3(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo3: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo3);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 4 from config service
     *
     * @returns the legal info line 4
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo4(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo4: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo4);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 5 from config service
     *
     * @returns the legal info line 5
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo5(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo5: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo5);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 6 from config service
     *
     * @returns the legal info line 6
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo6(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo6: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo6);
        return model ? model.value : '';
    }

    /**
     * get the legal info line 7 from config service
     *
     * @returns the legal info line 7
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo7(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: configKeyCorpInfoLegalInfo7: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfo7);
        return model ? model.value : '';
    }

    /**
     * get the legal info policy Label from config service
     *
     * @returns the legal info policy Label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoPolicy(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoPolicy: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfoPolicy);
        return model ? model.value : '';
    }

    /**
     * get the legal info policy link from config service
     *
     * @returns the legal info policy link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoPolicyLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoPolicyLink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfoPolicyLink);
        return model ? model.value : '';
    }

    /**
     * get the legal info TOU label from config service
     *
     * @returns the legal info TOU label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoTOU(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoTOU: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfoTOU);
        return model ? model.value : '';
    }

    /**
     * get the legal info TOU link from config service
     *
     * @returns the legal info TOU link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoTOULink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoTOULink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalInfoTOULink);
        return model ? model.value : '';
    }

    /**
     * get the legal info policy month from config service
     *
     * @returns the legal info policy month
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalPolicyMonth(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalPolicyMonth: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCorpInfoLegalPolicyMonth);
        return model ? model.value : '';
    }

    /**
     * get the global storage location from config service
     *
     * @returns the global storage location
     * @throws {Error} if service is not initialized
     */
    public getGlobalStorageLocation(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getGlobalStorageLocation: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyGlobalStorageLocation);
        return model && model.value.length > 0 ? model.value.split(',') : [];
    }

    /**
     * get the global storage location for Material Pricing screen
     *
     * @returns the global storage location for Material Pricing screen
     * @throws {Error} if service is not initialized
     */
    public getGlobalStorageLocationMatPricing(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getGlobalStorageLocationMatPricing: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyGlobalStorageLocationMatPricing);
        return model && model.value.length > 0 ? model.value.split(',') : [];
    }

    public getGlobalStorageLocationMatRequest(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getGlobalStorageLocationMatRequest: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyGlobalStorageLocationMatRequest);
        return model ? model.value : '';
    }

    /**
     * get the Commercial Info default text from config service
     *
     * @returns the Commercial Info default text
     * @throws {Error} if service is not initialized
     */
    public getCIDefaultText(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCIDefaultText: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCIDefaultText);
        return model ? model.value : '';
    }

    /**
     * get the Previous days limit the timesheet can be filled up
     *
     * @returns the Previous days limit the timesheet can be filled up
     * @throws {Error} if service is not initialized
     */
    public getPreviousDaysTimesheetLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getPreviousDaysTimesheetLimit: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyPreviousDaysTimesheetLimit);
        return model ? parseInt(model.value, 10) : 0;
    }

    /**
     * get the maximum allowed future time(in minutes) the timesheet can be submitted
     *
     * @returns the maximum allowed future time(in minutes) the timesheet can be submitted
     * @throws {Error} if service is not initialized
     */
    public getMaxAllowedFutureTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxAllowedFutureTime: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaxAllowedFutureTime);
        return model ? parseInt(model.value, 10) : 0;
    }

    /**
     * get the default followup time for NGMP58
     *
     * @returns the default followup time for NGMP58
     * @throws {Error} if service is not initialized
     */
    public getDefaultFollowUpTimeP58(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultFollowUpTimeP58: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFollowupDefaultTimeP58);
        return model ? model.value : '0';
    }

    /**
     * get the flag to switch on using the endpoint for customised forms
     *
     * @returns the flag to switch on using the endpoint for customised forms
     * @throws {Error} if service is not initialized
     */
    public getUseCustomisedForms(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCustomisedForms: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyUseCustomisedForms);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Get the Legal requirement checks for Germany in-vitro
     *             --Text--                       --Code--
     * Checkbox Checked according VDE0701/0702      VDE1
     *       Protective Conductor Resistant in mOhm VDE2
     *       Equivalent Device leakage in mA        VDE3
     *       Insulation Resistant in MOhm           VDE4
     *
     * @returns the legal check VDE0701/0702 text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckVDE701702Text(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDE701702Text: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDE701702Text);
        return model ? model.value : '';
    }

    public getLegalCheckVDE701702Code(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDE701702Code: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDE701702Code);
        return model ? model.value : '';
    }

    public getLegalCheckVDEPCResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEPCResistanceText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDEPCResistanceText);
        return model ? model.value : '';
    }

    public getLegalCheckVDEPCResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEPCResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDEPCResistanceCode);
        return model ? model.value : '';
    }

    public getLegalCheckVDEDeviceLeakageText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEDeviceLeakageText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDEDeviceLeakageText);
        return model ? model.value : '';
    }

    public getLegalCheckVDEDeviceLeakageCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEDeviceLeakageCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDEDeviceLeakageCode);
        return model ? model.value : '';
    }

    public getLegalCheckVDEInsulationResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEInsulationResistanceText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDEInsulationResistanceText);
        return model ? model.value : '';
    }

    public getLegalCheckVDEInsulationResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEInsulationResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVDEInsulationResistanceCode);
        return model ? model.value : '';
    }

    /**
     * get the predefined Email Domain Name for the additional emails component.
     *
     * @returns the predefined Email Domain Name
     * @throws {Error} if service is not initialized
     */
    public getPredefinedEmailDomainName(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getPredefinedEmailDomainName: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyPredefinedEmailDomainName);
        return model ? model.value : '';
    }

    // get the flag to switch on remove 9999 from error code
    public getUseNoCustomErrorCode(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseNoCustomErrorCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyUseNoCustomErrorCode);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // get the flag to switch on Add Material Return
    public getUseAddNewMaterialReturn(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseAddNewMaterialReturn: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyUseAddNewMaterialReturn);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // get the report text length as defined for Sales org / Plant
    public getReportTextLineLength(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getReportTextLineLength: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyReportTextLineLength);
        return model ? parseInt(model.value, 10) : ConfigConstants.defaultLineLength;
    }

    // get the report text max num of allowed lines as defined for Sales org / Plant
    public getReportTextMaxLines(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getReportTextMaxLines: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyReportTextMaxLines);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    public getMaxRecipientEntries(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxRecipientEntries: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaxRecipientEntries);
        return model ? parseInt(model.value, 10) : ConfigConstants.maxRecipientEntries;
    }

    public getSelectDefaultFollowupReason(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSelectDefaultFollowupReason: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeySelectDefaultFollowupReason);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getEnableSafetyAssessmentForm(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableSafetyAssessmentForm: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableSafetyAssessmentForm);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getBootstockReplenishEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getBootstockReplenishEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyBootstockReplenishEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getSafetyAssessmentForm(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSafetyAssessmentForm: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeySafetyAssessmentForm);
        return model ? model.value : '';
    }

    public getEnablePaceFormsCrossDeviceSync(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnablePaceFormsCrossDeviceSync: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnablePaceFormsCrossDeviceSync);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayAdditionalMaterialRequestInformation(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalMaterialRequestInformation: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayAdditionalMatReqInfo);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getAddMaterialCurrencyCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialCurrencyCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAddMaterialCurrencyCode);
        return model ? model.value : '';
    }

    public getAddMaterialSelectBillableDefault(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialSelectBillableDefault: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAddMaterialSelectBillableDefault);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getAddMaterialBillingIndicator(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialBillingIndicator: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAddMaterialBillingIndicator);
        return model ? model.value : '';
    }

    public getAddMaterialBillingChangeable(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialBillingChangeable: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAddMaterialBillingChangeable);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowCommercialInfo(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCommercialInfo: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowCommercialInfo);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    /**
     * Gets the CSML Tracker Link to track the material Ordered
     *
     * @returns the CSML Tracker Link
     * @throws {Error} if service is not initialized
     */
    public getCSMLTrackerLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCSMLTrackerLink: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCSMLTrackerLink);
        return model ? model.value : '';
    }

    public getAddDeliveryAddressTaxi(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayDeliveryAddressTaxi: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAddDeliveryAddressTaxi);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getDisablePaceForms(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDisablePaceForms: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisablePaceForms);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayNonProductiveHours(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayNonProductiveHours: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayNonProductiveHours);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getDisplayAdditionalTravelBack(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalTravelBack: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayAdditionalTravelBack);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayTravelBackSummary(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTravelBackSummary: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayTravelBackSummary);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayAdditionalTravelInfo(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalTravelInfo: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayAdditionalTravelInfo);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayMeansOfTransport(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayMeansOfTransport: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayMeansOfTransport);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayAdditionalMaterialReturnInformation(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalMaterialReturnInformation: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayAdditionalMatReturnInfo);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getFormatMultilineTextPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFormatMultilineTextPattern: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFormatMultilineTextPattern);
        return model ? model.value : '';
    }

    public getFormatInternalInfoMultilineTextPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFormatInternalInfoMultilineTextPattern: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFormatInternalInfoMultilineTextPattern);
        return model ? model.value : '';
    }

    public getDivisionsWithAdditionalLegalText(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithAdditionalLegalText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDivisionsWithAdditionalLegalText);
        return model && division.length > 0 ? model.value.includes(division) : false;
    }

    public getDivisionsWithIEC62353(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithIEC62353: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDivisionsWithIEC62353);
        return model && division.length > 0 ? model.value.includes(division) : false;
    }

    public getDivisionsWithVDE701702(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithVDE701702: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDivisionsWithVDE701702);
        return model && division.length > 0 ? model.value.includes(division) : false;
    }

    /**
     * Get the form types for which the participant copy workflow should be enabled for a given notification type.
     * Returns a string array instead of FormsConstants.FORMTYPES
     * a) to avoid a cyclic dependency
     * b) since the values from GW are given as strings anyway, so it does not matter where we cast them
     *
     * @param notificationType The notification type for which to get the supported form types
     * @returns string[] A list of form types that should support the participant copy workflow for this notification type
     * @throws {Error} if service is not initialized
     */
    public getParticipantCopySupportByNotificationType(notificationType: ConfigConstants.NotificationTypes): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getParticipantCopySupportByNotificationType: Trying to call ConfigService before initialization!`,
            );
        }

        try {
            const formTypes = this.getConfigModelByKey(
                ConfigConstants.configKeyPaceFormsTypesForParticipantCopy,
            )?.value;
            if (formTypes) {
                const support = JSON.parse(formTypes) as ConfigParticipantCopySupportByNotificationType;
                return support[notificationType] ?? [];
            }
        } catch (error) {
            this.log.error(
                `ConfigService: getParticipantCopySupportByNotificationType: error while parsing values for key '${
                    ConfigConstants.configKeyPaceFormsTypesForParticipantCopy
                }': ${JSON.stringify(error)}`,
            );
        }

        return [];
    }

    /**
     * Get the config to determine if moment need to be created in UTC or no
     *
     * @returns true if moment need to be created in UTC
     * @throws {Error} if service is not initialized
     */
    public getFormatMomentInUTC(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getFormatMomentInUTC: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyFormatMomentInUTC);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getDisplayTimesheetAddress(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTimesheetAddress: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayTimesheetAddress);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getTravelBackStartEditable(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTravelBackStartEditable: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyTravelBackStartEditable);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getTravelBackEndEditable(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTravelBackEndEditable: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyTravelBackEndEditable);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getTimesheetEndTimeLimit(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetEndTimeLimit: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyTimesheetEndTimeLimit);
        return model ? model.value : '';
    }

    public getDisplayTimesheetAddressOptions(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTimesheetAddressOptions: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayTimesheetAddressOptions);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getTimesheetAddressHome(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetAddressHome: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyTimesheetAddressHome);
        return model ? model.value : '';
    }

    public getTimesheetAddressOffice(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetAddressOffice: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyTimesheetAddressOffice);
        return model ? model.value : '';
    }

    public getMatPrioritiesForDOP(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatPrioritiesForDOP: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatPrioritiesForDOP);
        return model ? model.value : '';
    }

    public getMatPrioritiesForCustomer(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatPrioritiesForCustomer: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatPrioritiesForCustomer);
        return model ? model.value : '';
    }

    public getDisplayMaterialQuote(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayMaterialQuote: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayMaterialQuote);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayCustomerSignatureInForm(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCustomerSignatureInForm: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayCustomerSignatureInForm);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getTimesheetPauseCodes(taskType: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetPauseCodes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyTimesheetPauseCodes);
        return model && taskType.length > 0 ? model.value.includes(taskType) : false;
    }

    public getSparePartsCatalogLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSparePartsCatalogLink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeySparePartsCatalogLink);
        return model ? model.value : '';
    }

    public getDisableQuoteForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisableQuoteForNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisableQuoteForNotificationTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public getEscalateNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEscalateNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEscalateNotificationTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : true;
    }

    public getDefaultReasonCodeForCentralScrap(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultReasonCodeForCentralScrap: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDefaultReasonCodeForCentralScrap);
        return model ? model.value : '';
    }

    public getDisableSpecialTransportAWBfield(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisableSpecialTransportAWBfield: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisableSpecialTransportAWBfield);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getSpecialDangerousGoodsInd(dangerousGoodInd: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSpecialDangerousGoodsInd: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeySpecialDangerousGoodsInd);
        return model && dangerousGoodInd.length > 0 ? model.value.includes(dangerousGoodInd) : false;
    }

    public getDangerousGoodsIndicator(dangerousGoodInd: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDangerousGoodsIndicator: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDangerousGoodsInd);
        return model && dangerousGoodInd.length > 0 ? model.value.includes(dangerousGoodInd) : false;
    }

    public getFollowUpDefaultDays(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowUpDefaultDays: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyFollowUpDefaultDays);
        return model ? parseInt(model.value, 10) : 1;
    }

    /**
     * Determines whether to show Planned ETA alert for the notifications based on the value from the config set
     *
     * @returns true if the alert should be shown
     * @throws {Error} if service is not initialized
     */
    public getDisplayEtaAlert(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDisplayEtaAlert: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayEtaAlert);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * get the timer limit(in minutes) for the Alert message to be displayed before the Planned ETA
     *
     * @returns the timer limit(in minutes) for the Alert message to be displayed before the Planned ETA
     * @throws {Error} if service is not initialized
     */
    public getEtaAlertTimerLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEtaAlertTimerLimit: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEtaAlertTimerLimit);
        return model ? parseInt(model.value, 10) : ConfigConstants.defaultEtaAlertTimerLimit;
    }

    public getMileageHistoryLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMileageHistoryLimit: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMileageHistoryLimit);
        return model ? parseInt(model.value, 10) : ConfigConstants.defaultMileageHistoryDays;
    }

    public getUseMileageHistory(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getUseMileageHistory: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyUseMileageHistory);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDefaultMileageReportRecipients(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultMileageReportRecipients: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDefaultMileageReportRecipients);
        return model ? model.value : '';
    }

    public getDefaultMileageReportMailSubject(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultMileageReportMailSubject: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDefaultMileageReportMailSubject);
        return model ? model.value : '';
    }

    public getTextClausePattern(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getTextClausePattern: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyTextClausePattern);
        return model ? model.value : '';
    }

    public getShowMobileNumberForParticipants(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowMobileNumberForParticipants: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowMobileNumberForParticipants);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowCalibrationDate(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCalibrationDate: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowCalibrationDate);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getUseCustomTimeInput(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCustomTimeInput: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyUseCustomTimeInput);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowCustomerInformationOnSummary(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCustomerInformationOnSummary: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowCustomerInformationOnSummary);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getFormatMultilineTextOnBlur(): boolean {
        if (!this.isInitialized) {
            // This function can be called before initialization.
            // Example: In Report an Issue screen, we use text-input component, which calls this function.
            // User can report issue in app start before initialization step.
            // So, instead of throwing error, it will return false(default value).
            this.log.info(
                `ConfigService: getFormatMultilineTextOnBlur: Tried to call ConfigService before initialization!`,
            );
            return false;
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyFormatMultilineTextOnBlur);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowFormCustomerEmailData(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowFormCustomerEmailData: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowFormCustomerEmailData);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getUseCustomDurationStepper(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCustomDurationStepper: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyUseCustomDurationStepper);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getPrefillTroubleText(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getPrefillTroubleText: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyPrefillTroubleText);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getEnableJobOnFly(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableJobOnFly: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableJobOnFly);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getJobOnFlyDefaultDuration(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getJobOnFlyDefaultDuration: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyJobOnFlyDefaultDuration);
        return model ? parseInt(model.value, 10) : ConfigConstants.jobOnFlyDefaultDuration;
    }

    public getDisplayMaterialPricing(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayMaterialPricing: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayMaterialPricing);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getActivateCosima(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getActivateCosima: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyActivateCosima);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayBatchNumber(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayBatchNumber: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayBatchNumber);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayWeeklyWorkTime(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayWeeklyWorkTime: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayWeeklyWorkTime);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableStandbyTravel(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableStandbyTravel: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableStandbyTravel);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getIsTravelBillable(orderType: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getIsTravelBillable: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyIsTravelBillable);
        return model && orderType.length > 0 ? model.value.includes(orderType) : false;
    }

    public getDisplayCentralScrap(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCentralScrap: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayCentralScrap);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getDisplayCentralScrapForDangerousGoods(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCentralScrapForDangerousGoods: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayCentralScrapDangerGoods);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getSmartSensorTracker(): string[] {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: isSmartSensorTracker: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeySmartSensorTracker);
        return model && model.value.length > 0 ? model.value.split(',') : [''];
    }

    public getSmartSensorTrackerUrl(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSmartSensorTrackerUrl: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeySmartSensorTrackerUrl);
        return model ? model.value : '';
    }

    public getMatSerTypeaheadToggle(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatSerTypeaheadToggle: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatSerTypeaheadToggle);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getMatLTextDisplayLines(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatLTextDisplayLines: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatLTextDisplayLines);
        return model ? model.value.split(',') : [];
    }

    public getDisplayAdditionalTravelCost(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalTravelCost: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayAdditionalTravelCost);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getAdditionalTravelCostText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAdditionalTravelCostText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAdditionalTravelCostText);
        return model ? model.value : '';
    }

    public getMaxWeeklyWorkingHours(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxWeeklyWorkingHours: Trying to call ConfigService before initialization!`,
            );
        }
        const defaultMaxWeeklyWorkingHours = 50;
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaxWeeklyWorkingHours);
        return model ? parseInt(model.value, 10) : defaultMaxWeeklyWorkingHours;
    }

    public getDisplayCseSignature(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCseSignature: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayCseSignature);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayBootStockIndicator(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayBootStockIndicator: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayBootStockIndicator);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableBootstockScrap(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableBootstockScrap: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyBootStockScrapEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getAllowNotificationPause(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAllowNotificationPause: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyAllowNotificationPause);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayTrendCodeInFollowup(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTrendCodeInFollowup: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayTrendCodeInFollowup);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowMeasurementToolsDetails(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowMeasurementToolsDetails: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowMeasurementToolsDetails);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getHideParticipantFormSubmission(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideParticipantFormSubmission: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configHideParticipantFormSubmission);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowDataMediumInEquipment(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowDataMediumInEquipment: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowDataMediumInEquipment);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getAnaLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAnaLink: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyAnaLink);
        return model ? model.value : '';
    }

    public getShowIconForMaterialDelivery(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowIconForMaterialDelivery: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowIconForMaterialDelivery);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getTeamplayFleetLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getTeamplayFleetLink: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyTeamplayFleetLink);
        return model ? model.value : '';
    }

    public getComplaintsQuestionnaireLink(appID = ''): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getcomplaintsQuestionnaireLink${appID}: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyComplaintsQuestionnaireLink + appID);
        return model ? model.value : '';
    }

    public getSparePartCatalogMatLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSparePartCatalogMatLink: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeySparePartCatalogMatLink);
        return model ? model.value : '';
    }

    public getEnableTimesheetEmail(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableTimesheetEmail: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableTimesheetEmail);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public isMaterialLiBatteryStandAlone(unNumber = ''): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialLiBatteryStandAlone: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialLiBatteryStandAlone);
        return model && unNumber.length > 0 ? model.value.split(', ').some((value) => value === unNumber) : false;
    }

    public isMaterialLiBatteryInDevice(unNumber = ''): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialLiBatteryInDevice: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialLiBatteryInDevice);
        return model && unNumber.length > 0 ? model.value.split(', ').some((value) => value === unNumber) : false;
    }

    public isTimesheetDisabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: isTimesheetDisabled: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisableTimesheet);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDataManagementEmail(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDataManagementEmail: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDataManagementEmail);
        return model ? model.value : '';
    }

    public getDataManagementEmailInvitro(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDataManagementEmailInvitro: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDataManagementEmailInvitro);
        return model && model.value.length > 0 ? model.value : this.getDataManagementEmail();
    }

    public getShowFleetLinkInEquipmentHistory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowFleetLinkInEquipmentHistory: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.showFleetLinkInEquipmentHistory);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowStartImmediately(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowStartImmediately: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowStartImmediately);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    public getShowPlaceHolderForReportText(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowPlaceHolderForReportText: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowPlaceHolderForReportText);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowPlaceHolderForEscalationText(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowPlaceHolderForEscalationText: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowPlaceHolderForEscalationText);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getMaxWorkingHoursInTimesheet(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxWorkingHoursInTimesheet: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaxWorkingHoursInTimesheet);
        return model ? parseInt(model.value, 10) : ConfigConstants.totalWorkingHours;
    }

    /**
     * @deprecated The list of userStatus types does not change and does not need to be read from gateway; therefore no
     * longer using this function.
     *
     * Model value from configSet.json is comma separated string i.e. "PSI,PPI"
     * User status type in parameter is also comma separated string i.e. "PSI,REMT"
     * After splitting  ["PSI","REMT"]
     * Iterating above ("PSI,PPI").indexOf(PSI) is 0 returns true
     *
     * @param userStatusType - user status type
     * @returns true if userStatusType is in the model value
     */
    public getHideComplaintsForUserStatusType(userStatusType: ConfigConstants.USER_STATUS): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideComplaintsForUserStatusType: Trying to call ConfigService before initialization!`,
            );
        }
        const userStatusTypeArray = userStatusType.split(/,\s*|\s+/);
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideComplaintsForUserStatusType);
        return model && userStatusTypeArray.length > 0
            ? userStatusTypeArray.some((userStatus) => userStatus.length > 0 && model.value.includes(userStatus))
            : false;
    }

    public getBackendName(): string {
        if (!this.isInitialized) {
            throw new Error('ConfigService: getBackendName: Trying to call ConfigService before initialization!');
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyBackendName);
        return model ? model.value : '';
    }

    public isMaterialOrderPriorityForSplit(materialOrderPriority: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isMaterialOrderPriorityForSplit: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialOrderPriorityForSplit);
        return model && materialOrderPriority?.length > 0
            ? model.value.split(', ').some((value) => value === materialOrderPriority)
            : false;
    }

    public getDocboxDoctypes(): Array<{ key: string; value: string }> {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDocboxDoctypes: Trying to call ConfigService before initialization!`);
        }
        return this.models
            .filter((model) => model.key.startsWith('DocboxDoctype_'))
            .map((model) => ({ key: model.key.toUpperCase(), value: model.value }));
    }

    public getUserCountryFI(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getUserCountryFI: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.userCountryFI);
        return model ? model.value : '';
    }

    public getDisclaimerForChinaFI(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisclaimerForChinaFI: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.disclaimerForChinaFI);
        return model ? model.value.replace(/\\n/g, '\n') : '';
    }

    /**
     * SAP gateway does not support the full unicode character set. To avoid recurring problems with users entering
     * unsupported characters, we know check all text input for unsupported characters and mark them as error.
     * Since input fields can be used before the config service init was completed, we also return a default value in
     * those cases, e.g. to let users still report errors during startup of the application.
     *
     * Ideally we would also fix the naming here and on gateway to something like "getUnsupportedUnicodeRegex", but
     * what has been done has been done. Take this comment as explanation instead.
     *
     * @returns {string} - regex string to match unsupported unicode characters
     */
    public getEmojisRegex(): string {
        return this.emojisRegexString;
    }

    public getLegal3551NotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551NotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551NotificationTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public getLegal3551DetachPowerCordText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551DetachPowerCordText: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551DetachPowerCordText);
        return model ? model.value : '';
    }

    public getLegal3551PermanentConnectedText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLLegal3551PermanentConnectedText: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551PermanentConnectedText);
        return model ? model.value : '';
    }

    public getLegal3551NonDetachPowerCordText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551NonDetachPowerCordText: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551NonDetachPowerCordText);
        return model ? model.value : '';
    }

    public getLegal3551DetachPowerCordCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551DetachPowerCordCode: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551DetachPowerCordCode);
        return model ? model.value : '';
    }

    public getLegal3551PermanentConnectedCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLLegal3551PermanentConnectedCode: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551PermanentConnectedCode);
        return model ? model.value : '';
    }

    public getLegal3551NonDetachPowerCordCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551NonDetachPowerCordCode: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegal3551NonDetachPowerCordCode);
        return model ? model.value : '';
    }

    public getLegalVerificationNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerificationNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVerificationNotifTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public getDivisionsWithVerificationLegalCheck(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithVerificationLegalCheck: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDivisionsWithVerifLegalCheck);
        return model && division.length > 0 ? model.value.includes(division) : false;
    }

    public getLegalVerifiedSecurityDeviceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerifiedSecurityDeviceText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVerifiedSecurityDeviceText);
        return model ? model.value : '';
    }

    public getLegalVerifiedSecurityDeviceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerifiedSecurityDeviceCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalVerifiedSecurityDeviceCode);
        return model ? model.value : '';
    }

    public getLegalResumptionAfterUserValidationText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerifiedSecurityDeviceText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalResumptionUserValidText);
        return model ? model.value : '';
    }

    public getLegalResumptionAfterUserValidationCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalResumptionAfterUserValidationCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalResumptionUserValidCode);
        return model ? model.value : '';
    }

    public getLegalCheckHandoverSafetyInformationText(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getLegalCheckHandoverSafetyInformationText: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalHandoverSafetyInfoText);
        return model ? model.value : '';
    }

    public getLegalCheckHandoverUserDocumentationText(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getLegalCheckHandoverUserDocumentationText: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalHandoverUserDocText);
        return model ? model.value : '';
    }

    public getLegalCheckHandoverSafetyInformationCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getLegalCheckHandoverSafetyInformationCode: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalHandoverSafetyInfoCode);
        return model ? model.value : '';
    }

    public getLegalCheckHandoverUserDocumentationCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getLegalCheckHandoverUserDocumentationCode: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalHandoverUserDocCode);
        return model ? model.value : '';
    }

    public getDisplayConfirmSignatures(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayConfirmSignatures: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayConfirmSignatures);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableRemoteWork(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableRemoteWork: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableRemoteWork);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowRemoteStatusChangeWarning(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowRemoteStatusChangeWarning: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowRemoteStatusChangeWarning);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayDocBox(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDisplayDocBox: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayDocBox);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowUploadToDocBoxForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowUploadToDocBoxForNotificationType: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowUploadToDocBoxForNotificationType);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public getUploadToDocBoxMandatoryForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUploadToDocBoxMandatoryForNotificationType: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyUploadToDocBoxMandatoryForNotificationType);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public getUploadToDocBoxStatusMandatory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUploadToDocBoxStatusMandatory: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyUploadToDocBoxStatusMandatory);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getUploadToDocBoxAllowedMimeTypes(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUploadToDocBoxAllowedMimeTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyUploadToDocBoxAllowedMimeTypes);
        return model ? model.value.split(',') : this.defaultAllowedMimeTypes;
    }

    public getMatQuoteDistanceUnit(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatQuoteDistanceUnit: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatQuoteDistanceUnit);
        return model ? model.value : 'km';
    }

    public getEnableMatQuoteDistance(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMatQuoteDistance: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableMatQuoteDistance);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableHideTimesheetAddress(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableHideTimesheetAddress: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableHideTimesheetAddress);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getHideTimesheetPersonnelSubArea(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideTimesheetPersonnelSubArea: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideTimesheetPersonnelSubArea);
        return model ? model.value : '';
    }

    public getShowTpfReportVisibility(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowTpfReportVisibility: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowTpfReportVisibility);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getMandatoryTpfReportVisibility(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMandatoryTpfReportVisibility: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMandatoryTpfReportVisibility);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDefaultTpfReportVisibility(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultTpfReportVisibility: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDefaultTpfReportVisibility);
        const optionNAIndexForDefaultRadioSelection = 2;
        return model && model.value.length > 0 ? parseInt(model.value, 10) : optionNAIndexForDefaultRadioSelection;
    }

    public getEquipmentErrorCodeRemoteWork(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getEquipmentErrorCodeRemoteWork: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEquipmentErrorCodeRemoteWork);
        return model ? model.value : '';
    }

    public getDisplayConfirmationDistance(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getDisplayConfirmationDistance: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayConfirmationDistance);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayConfirmationExpenses(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getDisplayConfirmationExpenses: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayConfirmationExpenses);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDowntimeThresholdInMinutes(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDowntimeThresholdInMinutes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDowntimeThresholdInMinutes);
        return model && model.value.length > 0 ? parseInt(model.value, 10) : -1;
    }

    public getEnableIndividualDocBoxDownload(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getEnableIndividualDocBoxDownload: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableIndividualDocBoxDownload);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getPlaceHolderForCustomerIdentification(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getPlaceHolderForCustomerIdentification: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyPlaceHolderForCustomerIdentification);
        return model ? model.value : '---';
    }

    public getShowDataMaintenanceEnhancement(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getShowDataMaintenanceEnhancement: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowDataMaintenanceEnhancement);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayServicePartQuestionnaire(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayServicePartQuestionnaire: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayServicePartQuestionnaire);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getMaterialOrderTextInfoLineLength(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderTextInfoLineLength: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialOrderTextInfoLineLength);
        return model ? parseInt(model.value, 10) : ConfigConstants.defaultLineLength;
    }

    public getMaterialOrderTextInfoMaxLines(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderTextInfoMaxLines: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialOrderTextInfoMaxLines);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    public getMaterialOrderTextInfoPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderTextInfoPattern: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialOrderTextInfoPattern);
        return model ? model.value : '';
    }

    public getCSMLInvivoPhoneNumber(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getCSMLInvivoPhoneNumber: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCSMLInvivoPhoneNumber);
        return model ? model.value : '';
    }

    public getCSMLInvitroPhoneNumber(): string {
        if (!this.isInitialized) {
            throw new Error(
                'ConfigService: getCSMLInvitroPhoneNumber: Trying to call ConfigService before initialization!',
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCSMLInvitroPhoneNumber);
        return model ? model.value : '';
    }

    public getEnableTextInfoValidation(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableTextInfoValidation: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableTextInfoValidation);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getByBoxKonnectLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getByBoxKonnectLink: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyByBoxKonnectLink);
        return model ? model.value : '';
    }

    public getCBDocLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCBDocLink: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyCBDocLink);
        return model ? model.value : '';
    }

    public getSeSoLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getSeSoLink: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeySeSoLink);
        return model ? model.value : '';
    }

    public getSeSoDivisions(): string[] {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getSeSoDivisions: Trying to call ConfigService before initialization!`);
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeySeSoDivisions);
        return model && model.value.length > 0 ? model.value.split(',') : [];
    }

    public getEnableMatReturnWorkAround(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMatReturnWorkAround: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableMatReturnWorkAround);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayAddMaterialButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAddMaterialButton: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayAddMaterialButton);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableBillingIndicator(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableBillingIndicator: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableBillingIndicator);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDisplayCustomerPrice(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCustomerPrice: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplayCustomerPrice);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableManualIndividualization(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableManualIndividualization: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableManIndividualization);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableDocumentFeedbackButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableDocumentFeedbackButton: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableDocumentFeedbackButton);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableFormsHistoricalValues(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableFormsHistoricalValues: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableHistoricalValues);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getMeasurementToolsDefaultSettingsAPPS(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMeasurementToolsDefaultSettingsAPPS: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMeasurementToolsDefaultSettingsAPPS);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getHideRecoveryTimeForNotificationTypes(
        notificationType: ConfigConstants.NotificationTypes,
        notificationCategory: string[],
    ): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideRecoveryTimeForNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideRecoveryTimeForNotificationTypes);
        if (model && model.value.length > 0) {
            const notificationTypeAndCategoryArray = model.value.split(',');
            if (notificationTypeAndCategoryArray.length > 0) {
                return notificationTypeAndCategoryArray.some((item) => {
                    const trimmedValue = item.trim();
                    const two = 2;
                    if (trimmedValue.length === two) {
                        // example "MA"
                        return notificationType === trimmedValue;
                    } else {
                        // example "MI-IHOT"
                        const notificationTypeAndCategory = trimmedValue.split('-');
                        return (
                            notificationTypeAndCategory[0] === notificationType &&
                            notificationCategory.includes(notificationTypeAndCategory[1])
                        );
                    }
                });
            }
        }
        return false;
    }

    public getMaterialOrderDeliveryOptions(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderDeliveryOptions: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialOrderDeliveryOptions);
        return model ? model.value : '';
    }

    public getMaterialReturnAddressOptions(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialReturnAddressOptionss: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialReturnAddressOptions);
        return model ? model.value : '';
    }

    public getShowFCCForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowFCCForNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowFCCForNotificationTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public getBKInfoTextForBillingIndicatorsNotReceived(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getBKInfoTextForBillingIndicatorsNotReceived: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyBKInfoTextForBillingIndicatorsNotReceived);
        return model ? model.value : '';
    }

    public getShowCyberSecurityQuestion(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCyberSecurityQuestion: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowCyberSecurityQuestion);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getHideAbortAllFormsButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideAbortAllFormsButton: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideAbortAllFormsButton);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableAbortFormInList(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCyberSecurityQuestion: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableAbortFormInList);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Get the max char limit for Sub address fields i.e. Department, Floor, Room for Customer Selection in Material Request form
     * If value is not coming from GW Config Set then by default it would return value from constant in FE
     *
     * @returns {number} - max char limit for sub address fields
     * @throws {Error} if service is not initialized
     */
    public getMatRequestSubAddressCharLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatRequestSubAddressCharLimit: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatRequestSubAddressCharLimit);
        if (model) {
            return parseInt(model.value, 10);
        } else {
            this.log.warn(
                `ConfigService: getMatRequestSubAddressCharLimit: Not received any value from GW Config, so using value from FE`,
            );
            return ConfigConstants.subAddressMaxCharLimit;
        }
    }

    /**
     * Get the max char limit for contact fields i.e. Phone, Contact for Customer Selection in Material Request form
     * If value is not coming from GW Config Set then by default it would return value from constant in FE
     *
     * @returns {number} - max char limit for contact fields
     * @throws {Error} if service is not initialized
     */
    public getMatRequestContactCharLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatRequestContactCharLimit: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMatRequestContactCharLimit);
        if (model) {
            return parseInt(model.value, 10);
        } else {
            this.log.warn(
                `ConfigService: getMatRequestContactCharLimit: Not received any value from GW Config, so using value from FE`,
            );
            return ConfigConstants.contactMaxCharLimit;
        }
    }

    public getCommercialInfMandatoryForNonBill(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCommercialInfMandatoryForNonBill: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyCommercialInfMandatoryForNonBill);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableTextInfoForDop(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableTextInfoForDop: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableTextInfoForDop);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getHideSubmitButtonForForms(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideSubmitButtonForForms: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideSubmitButtonForForms);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getHideAuthorsFormsButtons(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideAuthorsFormsButtons: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideAuthorsFormsButtons);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowBootOtherStockWarning(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowBootOtherStockWarning: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowBootOtherStockWarning);
        return model && taskGroup.length > 0 ? model.value.includes(taskGroup) : true;
    }

    /**
     * Gets the LegalCheckDosageCode to display for equipment checkbox only for Country-Spain
     *
     * @returns the LegalCheckDosageCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDosageCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDosageCode: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalDosageCode);
        return model ? model.value : '';
    }

    /**
     * Gets the LegalCheckDosageText to compare with Equipment.Division only for Country-Spain
     *
     * @returns the LegalCheckDosageText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDosageText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDosageText: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyLegalDosageText);
        return model ? model.value : '';
    }

    public getEnableStatesOfUSADropdown(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableStatesOfUSADropdown: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableStatesOfUSADropdown);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Gets the DeliveryOptionsEnabled to enable priority shipping logic
     *
     * @returns the DeliveryOptionsEnabled
     * @throws {Error} if service is not initialized
     */
    public getDeliveryOptionsEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDeliveryOptionsEnabled: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDeliveryOptionsEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Gets the DeliveryApprovalMinimumLength to get minimum length for customer approver in material order screen
     *
     * @returns the DeliveryApprovalMinimumLength
     * @throws {Error} if service is not initialized
     */
    public getDeliveryApprovalMinimumLength(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDeliveryApprovalMinimumLength: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDeliveryApprovalMinimumLength);
        return model ? parseInt(model.value, 10) : ConfigConstants.defaultCustomerApproverLength;
    }

    public isCriticalityApplicableOnNotifType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isCriticalityApplicableOnNotifType: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyCriticalityNotifTypes);
        return model && notificationType.length > 0 ? model.value.includes(notificationType) : false;
    }

    public isCustomerDamageEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isCustomerDamageEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyCustomerDamageEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public isFocReplacementEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isFocReplacementEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyFocReplacementEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnabledAfterHoursCallOut(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnabledAfterHoursCallOut: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableAfterHourCallOut);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableOtherTasks(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableOtherTasks: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableOtherTasks);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getOtherTasksOverlapUpcomingNoti(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getOtherTasksOverlapUpcomingNoti: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyGetOtherTasksOverlapUpcomingNotification);
        return model ? model.value === ConfigConstants.sapTrue : true;
    }

    /**
     * Gets the DisplaySideMenuDocBoxFlag to enable DocBox
     * entry in the side menu
     *
     * @returns the DisplaySideMenuDocBoxFlag
     * @throws {Error} if service is not initialized
     */
    public getDisplaySideMenuDocBoxFlag(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplaySideMenuDocBoxFlag: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDisplaySideMenuDocBoxFlag);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getReportAnIssueAllowedMimeTypes(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getReportAnIssueAllowedMimeTypes: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyReportAnIssueAllowedMimeTypes);
        return model ? model.value.split(',') : this.defaultAllowedMimeTypes;
    }

    /**
     * Countries will have the possibility to select participant's attendee (credit) type
     * between Non-credit or Credit or N/A when adding a new attendee => radio buttons
     * Current business rule is only PaceUS will have this selector
     * enabled
     *
     * @returns the boolean representing the ParticipantCreditTypeSelectorEnabled enabled
     * @throws {Error} if config service is not initialized
     */
    public getParticipantCreditTypeSelectorEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getParticipantCreditTypeSelectorEnabled: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyParticipantCreditTypeSelectorEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableDurationChange(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableDurationChange: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableDurationChange);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    /**
     * Gets the value for Delivery Cut off time enabled
     *
     * @returns the getEnableDeliveryCutoffTime boolean
     * @throws {Error} if service is not initialized
     */
    public getEnableDeliveryCutoffTime(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableDeliveryCutoffTime: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDeliveryCutoffTimeEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getDocMissingProtocolsEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDocMissingProtocolsEnabled: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDocMissingProtocolsEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableFDA2579Form(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableFDA2579Form: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableFDA2579Form);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public isSourceReplacementEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isSourceReplacementEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableSourceReplacement);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getCentricarePCHEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCentricarePCHEnabled : Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCentricarePCHEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getCentricarePCHWarningTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCentricarePCHEnabled : Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyCentricarePCHWarningTime);
        return model ? parseInt(model.value, 10) : ConfigConstants.initialCentricarePCHWarningTime;
    }

    public getRadioactiveSurveyEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyEnabled: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyRadioactiveSurveyEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getRadioactiveSurveyDivisions(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyDivisions: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyRadioactiveSurveyDivisions);
        return model && division.length > 0 ? model.value.includes(division) : false;
    }

    public getRadioactiveSurveyToolCalibration(): number | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyToolCalibration: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyRadioactiveSurveyToolCalibration);
        return model ? parseFloat(model.value) : undefined;
    }

    public getRadioactiveSurveyRegion(region: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyRegion: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyRadioactiveSurveyRegions);
        return model && region && region.length > 0 ? model.value.includes(region) : false;
    }

    public getPickupRFIDEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getPickupRFIDEnabled: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyMaterialPickupRFIDEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableAWBPackaging(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableAWBPackaging: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAWBPackagingEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableFedexAwbPackagingScanning(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableFedexAwbPackaging: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableFedexAwbPackaging);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableInstallationPaperwork(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableInstallationPaperwork: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyInstallationPaperworkEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getIseCseMetricFormPowerApp(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getIseCseMetricFormPowerApp: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyIseCseMetricFormAppLink);
        return model ? model.value : '';
    }

    public getEnableCommercialInfoMandatory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableCommercialInfoMandatory: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableCommercialInfoMandatory);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowRemoteWork(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getShowRemoteWork: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowRemoteWork);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getShowStartImmediatelyFollowUp(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowStartImmediatelyFollowUp: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyShowStartImmediatelyFollowUp);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnablePhysicalInventory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnablePhysicalInventory: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyPhysicalInventoryEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEarlyTravelHours(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEarlyTravelHours: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEarlyTravelHours);
        if (!model || model.value === '') {
            return 0;
        }
        const hours = Number(model.value);
        return isNaN(hours) ? 0 : hours;
    }

    public getRentalToolAllowWithNoConsumption(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: configKeyRentalToolAllowWithNoConsumption: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyRentalToolAllowWithNoConsumption);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableBootstockReturn(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableBootstockReturn: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyBootstockReturnEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingForPartners(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingForPartners: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingForPartners);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingEnabled: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingEnabled);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingMinDate(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingMinDate: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingMinDate);
        return model ? parseInt(model.value, 10) : 0;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingMaxDate(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingMaxDate: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingMaxDate);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingMaxDuration(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingMaxDuration: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingMaxDuration);
        return model ? parseInt(model.value, 10) : Infinity;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingShowDedicated(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingShowDedicated: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingShowDedicated);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingDefaultDedicated(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingDefaultDedicated: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingDefaultDedicated);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    // do not delete this function. It will be used for appointment booking
    public getAppBookingITCS(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingITCS: Trying to call ConfigService before initialization!`);
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyAppBookingITCS);
        return model ? model.value : '';
    }

    public getEnableForceCheckin(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableForceCheckin: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableForceCheckin);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableMeasurementToolsForSCRM(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMeasurementToolsForSCRM: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableMeasurementToolsForSCRM);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getEnableUnlimitedHandover(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableUnlimitedHandover: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyEnableUnlimitedHandOver);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }
}
